import React, { useState, useEffect } from 'react';
import './NumberInput.css';

// `clearable`: an emptied box means "no value" and reports null, instead of
// snapping back to `min`.
const NumberInput = ({ value, onValueChange, displayZero = false, min = 0, max = 9999, size = 4, suffix = '', allowNegative = true, disabled = false, clearable = false }) => {
    // Always provide a controlled value
    const controlledValue = Number.isFinite(value) ? value : '';
    const [inputValue, setInputValue] = useState(String(controlledValue === '' ? '' : controlledValue));

    // Sync local state when value prop changes externally
    useEffect(() => {
        if (Number.isFinite(value)) {
            setInputValue(String(value));
        } else {
            setInputValue('');
        }
    }, [value]);

    const handleChange = (e) => {
        const rawValue = e.target.value;
        setInputValue(rawValue);

        // Allow empty string or just "-" for intermediate typing states
        if (rawValue === '' || rawValue === '-') {
            return;
        }

        let newValue = parseInt(rawValue, 10);
        if (!Number.isFinite(newValue)) {
            // If parsing fails, try to extract a valid number
            const extracted = rawValue.match(/-?\d+/);
            if (extracted) {
                newValue = parseInt(extracted[0], 10);
            } else {
                return; // Don't update parent if we can't parse
            }
        }

        // Clamp to max and min
        if (newValue > max) {
            newValue = max;
            setInputValue(String(newValue));
        }
        if (newValue < min) {
            newValue = min;
            setInputValue(String(newValue));
        }
        onValueChange(newValue);
    };

    const handleBlur = (e) => {
        const rawValue = e.target.value.trim();
        // If input is empty or just a minus sign, set to min value
        if (rawValue === '' || rawValue === '-') {
            if (clearable) {
                setInputValue('');
                if (Number.isFinite(value)) onValueChange(null);
                return;
            }
            setInputValue(String(min));
            onValueChange(min);
        }
    };

    const shouldSelectAllOnMobile = () => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia('(max-width: 62.4375rem)').matches;
    };

    const handleMobileSelectAll = (e) => {
        if (!shouldSelectAllOnMobile() || disabled) return;
        if (typeof e.target.select === 'function') {
            e.target.select();
        }
    };

    const getValueClass = () => {
        if (!Number.isFinite(controlledValue)) return '';
        if (controlledValue > 0) return 'positive';
        if (controlledValue < 0) return 'negative';
        return '';
    };

    return (
        <div className="number-input-container">
            <input
                type="text"
                inputMode="numeric"
                maxLength={size + 1}
                className={`number-input s${size} ${getValueClass()}`}
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleMobileSelectAll}
                onClick={handleMobileSelectAll}
                placeholder={clearable ? '—' : `${min}`}
                disabled={disabled}
                onInput={(e) => {
                    // Only allow digits, minus sign at start (if allowed), and empty
                    if (allowNegative) {
                        e.target.value = e.target.value.replace(/[^-\d]/g, '');
                        // Only allow minus at the start
                        if (e.target.value.includes('-')) {
                            const minusIndex = e.target.value.indexOf('-');
                            if (minusIndex > 0) {
                                e.target.value = e.target.value.replace(/-/g, '');
                            }
                            // Only one minus sign
                            if ((e.target.value.match(/-/g) || []).length > 1) {
                                e.target.value = '-' + e.target.value.replace(/-/g, '');
                            }
                        }
                    } else {
                        // Don't allow minus sign at all
                        e.target.value = e.target.value.replace(/[^\d]/g, '');
                    }
                }}
            />
            {suffix && <span className="number-input-suffix">{suffix}</span>}
        </div>
    );
};

export default NumberInput;
